/**
 * JSON parsing that keeps SQL `bigint` and `decimal` values exact.
 *
 * `JSON.parse` turns every number into a double, which silently corrupts integers beyond 2^53
 * and decimals with more than ~15 significant digits. Using the reviver's access to the source
 * text (ES2025 "JSON.parse source text access"), such numbers are returned as their exact
 * digit strings instead. Ordinary numbers stay numbers.
 */

interface ReviverContext {
  source?: string;
}

/** Doubles represent every decimal with up to 15 significant digits exactly. */
const MAX_EXACT_DIGITS = 15;

function significantDigits(source: string): number {
  const mantissa = source.replace(/^[-+]/, '').split(/[eE]/)[0] ?? '';
  return mantissa.replace('.', '').replace(/^0+/, '').length;
}

function isLossy(value: number, source: string): boolean {
  if (/^-?\d+$/.test(source)) return !Number.isSafeInteger(value);
  return significantDigits(source) > MAX_EXACT_DIGITS;
}

function reviver(_key: string, value: unknown, context?: ReviverContext): unknown {
  return typeof value === 'number' &&
    context?.source !== undefined &&
    isLossy(value, context.source)
    ? context.source
    : value;
}

/** True when the runtime passes source text to JSON.parse revivers. */
export const supportsLosslessJson = (() => {
  let source: string | undefined;
  JSON.parse('1', (_key, value: unknown, context?: ReviverContext) => {
    source = context?.source;
    return value;
  });
  return source === '1';
})();

export function parseLosslessJson(text: string): unknown {
  return supportsLosslessJson
    ? (JSON.parse(text, reviver) as unknown)
    : (JSON.parse(text) as unknown);
}

const JSON_CONTENT_TYPE = /^application\/([\w.-]+\+)?json\b/i;

/** RTK Query `responseHandler` that parses JSON losslessly and leaves other bodies as text. */
export async function losslessJsonResponseHandler(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  return JSON_CONTENT_TYPE.test(response.headers.get('Content-Type') ?? '')
    ? parseLosslessJson(text)
    : text;
}
